/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, Text, TextInput, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { Ionicons } from '@expo/vector-icons';
import { ProminentSearchWithScanner } from '../../../shared/ui/ProminentSearchWithScanner'
import { BarcodeScannerModal } from '../../../shared/ui/BarcodeScannerModal';
import { useBranchBarcodeInputModeForCurrentBranch } from '../../../shell/session/hooks/useBranchBarcodeInputMode';
import { createBarcodeScanDedupe, looksLikeBarcodeHardwareSubmit } from '../../../shared/product-search/productBarcodeInput';
import { lookupProductByBarcode, searchProducts } from '../../../catalog/infra-api'
import type { ProductSearchRow } from '../../../catalog/domain-api';
import { styles } from '../ReportsScreen.styles';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { getReportsVentasLayoutMode, type ReportsVentasLayoutMode } from '../reportsVentasLayout';
import { ReportsVentasProductCatalogPickerModal } from './ReportsVentasProductCatalogPickerModal';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';
import { useDebouncedValue } from '../../../shared/ui/hooks-ui/useDebouncedValue';
import { useOperationalWebSearchFocus } from '../../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { ticketColors } from '../../../shared/theme/ticketColors';

/**
 * Selector local (ligero): escribís texto → sugerencias vía {@link searchProducts}; tocás una → pin por ID exacto al servidor.
 * Si después de buscar hay una sola sugerencia, se fija ese ítem automáticamente.
 * Para cambiar el ítem fijado, buscás sin borrar el filtro hasta elegir uno nuevo (la X solo quita el filtro).
 * SCAN igual que Catálogo/Stock; el listado grande sigue siendo opcional por ícono de catálogo.
 */
export function ReportsVentasProductSearchControls({
  vm,
  disabled,
  layout: layoutProp,
}: {
  vm: ReportsScreenViewModel;
  disabled: boolean;
  layout?: ReportsVentasLayoutMode;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [draftQuery, setDraftQuery] = useState('');
  const debouncedDraft = useDebouncedValue(draftQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: true,
  });
  const [suggestions, setSuggestions] = useState<ProductSearchRow[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const { width: windowWidth } = useWindowDimensions();
  const layout = layoutProp ?? getReportsVentasLayoutMode(windowWidth);
  const { catalogIconOnly, catalogLabel, scanShowIcon } = layout;

  const { allowCamera: barcodeAllowCamera, allowHardwareWedge: barcodeAllowHardware } =
    useBranchBarcodeInputModeForCurrentBranch();
  const ventasBarcodeDedupe = useMemo(() => createBarcodeScanDedupe(), []);
  const draftQueryRef = useRef(draftQuery);
  draftQueryRef.current = draftQuery;

  const hasPin = vm.reportsVentasPinnedProductId != null;

  useEffect(() => {
    if (debouncedDraft.length < MIN_PRODUCT_SEARCH_QUERY_CHARS) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    let cancelled = false;
    setSuggestionsLoading(true);

    void searchProducts({
      search: debouncedDraft,
      limit: 22,
      offset: 0,
      branchId: vm.currentBranchId ?? null,
      posExcludeMisc: true,
    })
      .then((rows) => {
        if (!cancelled) setSuggestions(rows.slice(0, 20));
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setSuggestionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedDraft, vm.currentBranchId]);

  const resolveCodeToPin = useCallback(
    async (raw: string, origin: string) => {
      const code = raw.trim();
      if (!code) return;
      try {
        const result = await lookupProductByBarcode({ code, includeInactive: true, allowRemoteLookup: true });
        if (result.kind === 'local_hit') {
          if (result.product.isMisc) {
            showInfo('No disponible', 'La línea «Varios» no se puede filtrar en informes.');
            return;
          }
          vm.pinVentasProductFromCatalog(result.product.id, result.product.name);
          setDraftQuery('');
          setSuggestions([]);
          return;
        }
        if (result.kind === 'remote_hit') {
          showInfo(
            'Sin producto cargado',
            'Hay datos para ese código pero todavía no está en tu catálogo. Crealo o sincronizalo desde Catálogo, o buscalo por nombre arriba.',
          );
          return;
        }
        if (result.kind === 'offline_miss') {
          showInfo('Sin conexión', 'Sin internet no se puede consultar el código.');
          return;
        }
        showInfo('Sin coincidencia', `No hay un producto cargado con ese código (${origin}).`);
      } catch (e: unknown) {
        const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Error';
        showError('Error', msg);
      }
    },
    [vm],
  );

  const handleVentasSearchSubmit = useCallback(() => {
    const q = draftQueryRef.current.trim();
    if (!q) return;
    if (looksLikeBarcodeHardwareSubmit(q) && !ventasBarcodeDedupe(q)) {
      return;
    }
    void resolveCodeToPin(q, 'lector o teclado');
  }, [resolveCodeToPin, ventasBarcodeDedupe]);

  const onPickSuggestion = useCallback(
    (p: ProductSearchRow) => {
      vm.pinVentasProductFromCatalog(p.id, p.name);
      setDraftQuery('');
      setSuggestions([]);
    },
    [vm],
  );

  /** Si el catálogo local devolvió una única coincidencia, mismo efecto que tocarla manualmente (o reemplazar el pin actual por otro). */
  useEffect(() => {
    if (disabled || suggestionsLoading) return;
    if (debouncedDraft.length < MIN_PRODUCT_SEARCH_QUERY_CHARS) return;
    if (suggestions.length !== 1) return;
    const only = suggestions[0];
    if (!only) return;
    if (only.id === vm.reportsVentasPinnedProductId) return;
    onPickSuggestion(only);
  }, [debouncedDraft, disabled, onPickSuggestion, suggestions, suggestionsLoading, vm.reportsVentasPinnedProductId]);

  const suggestionPanelOpen = debouncedDraft.length >= MIN_PRODUCT_SEARCH_QUERY_CHARS;

  const suggestionFooter =
    suggestionPanelOpen ? (
      <View style={styles.ventasSearchSuggestionsPanel}>
        {suggestionsLoading ? (
          <View style={{ padding: spacing.md, alignItems: 'center' }}>
            <ActivityIndicator color={ticketColors.ink2} accessibilityLabel="Buscando en catálogo local" />
          </View>
        ) : suggestions.length === 0 ? (
          <Text style={[styles.caption, { padding: spacing.md, color: ticketColors.ink3 }]}>
            No hay ítems que coincidan. Probá otro texto o usá SCAN si tenés el código de barras.
          </Text>
        ) : (
          <ScrollView keyboardShouldPersistTaps="handled" style={styles.ventasSearchSuggestionsScroll}>
            {suggestions.map((item) => {
              const sku = item.sku ? String(item.sku) : '';
              return (
                <TouchableOpacity
                  key={item.id}
                  style={[styles.salesListRow, { paddingVertical: spacing.sm, borderBottomColor: ticketColors.rule }]}
                  onPress={() => onPickSuggestion(item)}
                  disabled={disabled}
                  accessibilityRole="button"
                  accessibilityLabel={`Usar ${item.name} como único ítem del filtro`}
                >
                  <View style={styles.salesListRowLeft}>
                    <Text style={styles.salesListRowProducts} numberOfLines={2}>
                      {item.name}
                    </Text>
                    {sku ? <Text style={styles.salesListRowRef}>SKU · {sku}</Text> : null}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    ) : null;

  return (
    <>
      <View style={styles.ventasSearchBlock}>
      <ProminentSearchWithScanner
        inputRef={searchInputRef}
        unifiedField
        scanTone="neutral"
        scanShowIcon={scanShowIcon}
        variant="responsive"
        sectionLabel={null}
        value={draftQuery}
        onChangeText={(t) => {
          setDraftQuery(t);
          if (!t.trim()) {
            setSuggestions([]);
          }
        }}
        placeholder={`Nombre o código (min. ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres)… (F2)`}
        placeholderCompact={`Buscar ítem… (F2)`}
        accessibilityLabel={`Escribí al menos ${MIN_PRODUCT_SEARCH_QUERY_CHARS} caracteres para ver sugerencias del catálogo local, o escaneá un código. Atajos F2 o /`}
        disabled={disabled}
        showBarcodeCameraButton={barcodeAllowCamera}
        showHidHint={!barcodeAllowCamera}
        onSubmitEditing={handleVentasSearchSubmit}
        textInputProps={
          Platform.OS === 'web'
            ? ({
                onKeyDown: (e: { key?: string; preventDefault?: () => void }) => {
                  if (e?.key !== 'Enter') return;
                  e.preventDefault?.();
                  handleVentasSearchSubmit();
                },
                // RN TextInput typings omit DOM handlers; expo web maps this to the DOM input.
              } as {})
            : undefined
        }
        onPressScan={barcodeAllowCamera ? () => setBarcodeScannerOpen(true) : undefined}
        endSlot={
          <TouchableOpacity
            style={[
              styles.ventasCatalogPickerButton,
              catalogIconOnly && styles.ventasCatalogPickerButtonIconOnly,
              disabled && styles.disabled,
            ]}
            onPress={() => !disabled && setPickerOpen(true)}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel="Abrir listado del catálogo para elegir un ítem"
            accessibilityHint="Muestra todos los productos y servicios de la sucursal"
          >
            <Ionicons name="list-outline" size={catalogIconOnly ? 22 : 20} color={ticketColors.ink2} />
            {!catalogIconOnly ? <Text style={styles.ventasCatalogPickerButtonLabel}>{catalogLabel}</Text> : null}
          </TouchableOpacity>
        }
        footer={suggestionFooter}
      />
      </View>

      {hasPin ? (
        <View style={styles.ventasPinnedProductChip}>
          <View style={styles.ventasPinnedProductChipText}>
            <Text style={[styles.caption, { color: ticketColors.ink3 }]} numberOfLines={1}>
              Filtrando informes por
            </Text>
            <Text style={[styles.secondaryButtonText, { fontWeight: '600' }]} numberOfLines={2}>
              {vm.reportsVentasPinnedProductLabel ?? vm.reportsVentasPinnedProductId}
            </Text>
          </View>
          <TouchableOpacity
            onPress={vm.clearVentasProductPin}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel="Quitar filtro de producto"
            hitSlop={10}
          >
            <Ionicons name="close-circle" size={24} color={ticketColors.ink2} />
          </TouchableOpacity>
        </View>
      ) : null}

      <BarcodeScannerModal
        visible={barcodeScannerOpen}
        title="Escanear ítem"
        subtitle="Escaneá el código para filtrar por ese ítem."
        onClose={() => setBarcodeScannerOpen(false)}
        onCodeScanned={(code) => {
          const c = code.trim();
          setBarcodeScannerOpen(false);
          if (c) {
            setDraftQuery(c);
            setDraftQuery(c);
          }
          void resolveCodeToPin(code, 'cámara');
        }}
      />

      <ReportsVentasProductCatalogPickerModal
        visible={pickerOpen}
        branchId={vm.currentBranchId ?? null}
        onClose={() => setPickerOpen(false)}
        onSelect={(product) => {
          vm.pinVentasProductFromCatalog(product.id, product.name);
          setDraftQuery('');
          setSuggestions([]);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
